'use client'

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'

type CsrfContextValue = {
  token: string
  /** True once every automatic attempt has failed; forms can offer a retry. */
  failed: boolean
  /** Start a fresh round of attempts. Safe to call while one is in flight. */
  retry: () => void
}

const CsrfContext = createContext<CsrfContextValue | null>(null)

// Three tries covers a flaky first request without hammering the route; a
// form that is still tokenless after this shows the failure instead of
// sitting disabled forever.
const ATTEMPTS = 3
const BACKOFF_MS = [500, 1500]

/**
 * Shared CSRF token for every form on a page. One fetch, one cookie, one
 * token. Avoids the race where two forms both fire `/api/csrf` before either
 * response has set a cookie, causing the second Set-Cookie to overwrite the
 * first and leaving one form with a token that no longer matches the cookie.
 *
 * Wrap the form-bearing layout (or page) in <CsrfProvider>. Every form below
 * calls useCsrf() to read the shared token and gates its submit on it.
 */
export function CsrfProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = useState('')
  const [failed, setFailed] = useState(false)
  // One acquisition at a time. Every /api/csrf response sets the cookie the
  // token must match, so two overlapping fetches could leave the later cookie
  // paired with the earlier token.
  const pending = useRef<Promise<void> | null>(null)

  const acquire = useCallback(() => {
    if (pending.current) return pending.current
    const run = async () => {
      for (let attempt = 0; attempt < ATTEMPTS; attempt++) {
        try {
          const res = await fetch('/api/csrf')
          const data = (await res.json()) as { token?: string }
          if (typeof data.token === 'string' && data.token) {
            setToken(data.token)
            return
          }
        } catch {
          // fall through to the next attempt
        }
        const wait = BACKOFF_MS[attempt]
        if (wait !== undefined) await new Promise((r) => setTimeout(r, wait))
      }
      setFailed(true)
    }
    pending.current = run().finally(() => {
      pending.current = null
    })
    return pending.current
  }, [])

  useEffect(() => {
    void acquire()
  }, [acquire])

  const retry = useCallback(() => {
    setFailed(false)
    void acquire()
  }, [acquire])

  return <CsrfContext.Provider value={{ token, failed, retry }}>{children}</CsrfContext.Provider>
}

/**
 * Returns the shared CSRF token from the surrounding CsrfProvider. Throws
 * if used outside one, on purpose: it's cheap to wrap and easy to miss.
 */
export function useCsrf(): CsrfContextValue {
  const ctx = useContext(CsrfContext)
  if (!ctx) {
    throw new Error('useCsrf must be used within a CsrfProvider')
  }
  return ctx
}
